/**
 * Gomoku (Five in a Row) Game Engine
 * 15x15 standard grid, move validation, win detection
 */

export const BOARD_SIZE = 15;
export const EMPTY = 0;
export const PLAYER_BLACK = 1; // Moves first
export const PLAYER_WHITE = 2;

export class GomokuEngine {
  constructor() {
    this.reset();
  }

  reset() {
    this.board = Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(EMPTY));
    this.currentTurn = PLAYER_BLACK;
    this.moveHistory = [];
    this.lastMove = null;
    this.winner = null;
    this.isDraw = false;
    this.winningLine = null;
  }

  isValidMove(x, y) {
    if (this.winner || this.isDraw) return false;
    if (x < 0 || x >= BOARD_SIZE || y < 0 || y >= BOARD_SIZE) return false;
    return this.board[y][x] === EMPTY;
  }

  makeMove(x, y, player = this.currentTurn) {
    if (!this.isValidMove(x, y)) return false;
    if (player !== this.currentTurn) return false;

    this.board[y][x] = player;
    const move = { x, y, player, timestamp: Date.now() };
    this.moveHistory.push(move);
    this.lastMove = { x, y };

    const winResult = this.checkWin(x, y, player);
    if (winResult.win) {
      this.winner = player;
      this.winningLine = winResult.line;
    } else if (this.isBoardFull()) {
      this.isDraw = true;
    } else {
      this.currentTurn = this.currentTurn === PLAYER_BLACK ? PLAYER_WHITE : PLAYER_BLACK;
    }

    return true;
  }

  checkWin(x, y, player) {
    const directions = [
      { dx: 1, dy: 0 },  // Horizontal
      { dx: 0, dy: 1 },  // Vertical
      { dx: 1, dy: 1 },  // Diagonal \
      { dx: 1, dy: -1 }  // Anti-diagonal /
    ];

    for (const { dx, dy } of directions) {
      const line = [{ x, y }];

      // Positive direction
      let step = 1;
      while (true) {
        const nx = x + dx * step;
        const ny = y + dy * step;
        if (nx >= 0 && nx < BOARD_SIZE && ny >= 0 && ny < BOARD_SIZE && this.board[ny][nx] === player) {
          line.push({ x: nx, y: ny });
          step++;
        } else {
          break;
        }
      }

      // Negative direction
      step = 1;
      while (true) {
        const nx = x - dx * step;
        const ny = y - dy * step;
        if (nx >= 0 && nx < BOARD_SIZE && ny >= 0 && ny < BOARD_SIZE && this.board[ny][nx] === player) {
          line.push({ x: nx, y: ny });
          step++;
        } else {
          break;
        }
      }

      if (line.length >= 5) {
        return { win: true, line };
      }
    }

    return { win: false, line: null };
  }

  isBoardFull() {
    for (let y = 0; y < BOARD_SIZE; y++) {
      for (let x = 0; x < BOARD_SIZE; x++) {
        if (this.board[y][x] === EMPTY) return false;
      }
    }
    return true;
  }

  serialize() {
    return {
      board: this.board.map((row) => [...row]),
      currentTurn: this.currentTurn,
      moveHistory: [...this.moveHistory],
      lastMove: this.lastMove ? { ...this.lastMove } : null,
      winner: this.winner,
      isDraw: this.isDraw,
      winningLine: this.winningLine ? [...this.winningLine] : null
    };
  }

  restore(state) {
    if (!state) return;
    this.board = state.board.map((row) => [...row]);
    this.currentTurn = state.currentTurn;
    this.moveHistory = [...(state.moveHistory || [])];
    this.lastMove = state.lastMove ? { ...state.lastMove } : null;
    this.winner = state.winner;
    this.isDraw = state.isDraw;
    this.winningLine = state.winningLine ? [...state.winningLine] : null;
  }
}
